/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CSSProperties, useRef, useState, useCallback, useEffect } from "react";
import { css, cx } from "@panda/css";

import { OptimizedCosmosAssetFragment } from "../types";
import { useBackgroundBlur, useHygraphImageLoader } from "@components/hooks";

// Blur source is downsampled heavily by the canvas (downsampleFactor defaults to 8),
// so a small optimized variant is more than enough — avoids pulling the 1920px+ original
// just to feed a blurred backdrop. Matches the GraphQL `optUrl: resize(width:256)` hint.
const BLUR_SOURCE_WIDTH = 256;

const styles = css({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	position: "absolute",
	inset: "calc(var(--image-blur-pixels, 125px) * -2)",
	opacity: "var(--image-opacity, cover)",
	pointerEvents: "none",
	zIndex: 0,
});

const canvasStyles = css({
	display: "block",
	position: "absolute",
	inset: 0,
	w: "full",
	h: "full",
});

const hiddenImgStyles = css({
	position: "absolute",
	inset: 0,
	w: "full",
	h: "full",
	opacity: 0,
	pointerEvents: "none",
});

type ObjectFit = "fill" | "contain" | "cover" | "none" | "scale-down";

export interface BlurredImageProps {
	className?: string;
	image?: OptimizedCosmosAssetFragment | null;
	sourceElement?: HTMLImageElement | HTMLVideoElement | null;
	syncVideoRef?: React.RefObject<HTMLVideoElement | null>;
	blurPixels?: number;
	objectFit?: ObjectFit;
	opacity?: number;
	width: number;
	height: number;
	disableAutoplay?: boolean;
	saturation?: number;
	downsampleFactor?: number;
	scale?: number;
	distortionStrength?: number;
}

export interface BlurredImageCssVariables extends CSSProperties {
	"--image-blur-pixels"?: string;
	"--image-object-fit"?: ObjectFit;
	"--image-opacity"?: string;
}

export const BlurredImage = ({
	className,
	image,
	sourceElement,
	syncVideoRef,
	blurPixels = 50,
	objectFit = "cover",
	opacity = 1,
	width,
	height,
	disableAutoplay: _disableAutoplay = true,
	saturation = 1,
	downsampleFactor = 8,
	scale = 1,
	distortionStrength = 1,
}: BlurredImageProps): React.JSX.Element | null => {
	const [hasError, setHasError] = useState(false);
	const imageRef = useRef<HTMLImageElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const externalSourceRef = useRef<HTMLImageElement | HTMLVideoElement | null>(null);
	const hygraphImageLoader = useHygraphImageLoader();

	// `sourceElement === undefined` means "render your own hidden media".
	// `sourceElement === null` means "an external element is coming via a ref callback,
	// don't render a duplicate". This distinction prevents duplicate network requests
	// when the parent uses a shared media element pattern.
	const useExternalSource = sourceElement !== undefined;
	externalSourceRef.current = sourceElement ?? null;

	const isVideo = image?.mimeType?.startsWith("video/") ?? false;
	const sourceRef = useExternalSource ? externalSourceRef : isVideo ? videoRef : imageRef;

	useEffect(() => {
		if (useExternalSource) return;

		const hidden = videoRef.current;
		const main = syncVideoRef?.current;
		if (!hidden || !main) return;

		const sync = (): void => {
			if (Math.abs(hidden.currentTime - main.currentTime) > 0.1) {
				hidden.currentTime = main.currentTime;
			}
			if (main.paused && !hidden.paused) hidden.pause();
			if (!main.paused && hidden.paused) void hidden.play();
		};

		main.addEventListener("seeked", sync);
		main.addEventListener("play", sync);
		main.addEventListener("pause", sync);
		main.addEventListener("timeupdate", sync);

		sync();

		return () => {
			main.removeEventListener("seeked", sync);
			main.removeEventListener("play", sync);
			main.removeEventListener("pause", sync);
			main.removeEventListener("timeupdate", sync);
		};
	}, [syncVideoRef, useExternalSource]);

	const { hasError: blurError } = useBackgroundBlur({
		canvasRef,
		sourceRef,
		config: {
			blurPixels,
			saturation,
			downsampleFactor,
			scale,
			distortionStrength,
		},
		sourceElement: sourceElement ?? undefined,
	});

	const handleError = useCallback(() => {
		setHasError(true);
	}, []);

	if (!image?.url) return null;

	// Prefer the SSR-baked optUrl (fragments provide a 256/q50 variant); fall back to
	// routing through the runtime loader so the hidden <img> still pulls a resized
	// asset instead of the raw original. SVGs and videos keep their original url.
	const isSvg = image?.mimeType?.includes("svg") ?? false;
	const blurImageSrc =
		isVideo || isSvg
			? image.url
			: image.optUrl || hygraphImageLoader({ src: image.url, width: BLUR_SOURCE_WIDTH }) || image.url;

	const styleVars: BlurredImageCssVariables = {
		"--image-object-fit": objectFit,
		"--image-opacity": `${opacity}`,
		"--image-blur-pixels": `${blurPixels}px`,
		width: "unset",
		display: hasError || blurError ? "none" : undefined,
	};

	return (
		<div className={cx("blurred-image", className, styles)} style={styleVars}>
			{!useExternalSource &&
				(isVideo ? (
					<video
						ref={videoRef}
						src={image.url}
						crossOrigin="anonymous"
						aria-hidden="true"
						className={hiddenImgStyles}
						autoPlay
						muted
						loop
						playsInline
						preload="auto"
						onError={handleError}
					/>
				) : (
					<img
						ref={imageRef}
						src={blurImageSrc}
						crossOrigin="anonymous"
						aria-hidden="true"
						alt=""
						className={hiddenImgStyles}
						width={width || 1792}
						height={height || 810}
						onError={handleError}
					/>
				))}
			<canvas ref={canvasRef} aria-hidden="true" className={cx("blurred-image__canvas", canvasStyles)} />
		</div>
	);
};
